import { SVGProps, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '@deliveryhero/pd-cookbook/components/Button';
import { ButtonCircular } from '@deliveryhero/pd-cookbook/components/ButtonCircular';
import IcClose from '@deliveryhero/pd-cookbook/components/Icons/IcClose';
import IcWallet from '@deliveryhero/pd-cookbook/components/Icons/IcWallet';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';

import ActionMenu from 'components/ActionMenu';
import CallToActions from 'components/CallToActions';
import { CsvUpload } from 'components/CsvUpload';
import { CsvColumn } from 'components/CsvUpload/types';
import { FormLabelText } from 'components/Forms/GiftCardPurchase/constants';
import { GiftCardPurchaseFormValues } from 'components/Forms/GiftCardPurchase/types';
import Page, { ActionType } from 'components/Page';
import PageHeader from 'components/PageHeader/PageHeader';
import SearchInput from 'components/SearchInput';
import { PendingVerification } from 'components/svg/PendingVerification';
import ReferAFriend from 'components/svg/ReferAFriend';
import TableList, { RowsPerPage, TableFilterRow } from 'components/TableList';
import { Column } from 'components/TableList/SortableHeader';
import { useConfiguration } from 'contexts/country-config';
import { FormikHelpers, FormikProps } from 'formik';
import useAction from 'hooks/useAction';
import useApi from 'hooks/useApi';
import useNotification from 'hooks/useNotification';
import useQueryParam from 'hooks/useQueryParam/useQueryParam';
import useQueryWrapperP0 from 'hooks/useQueryWrapperP0';
import useQueryWrapperP1 from 'hooks/useQueryWrapperP1';
import useUser from 'hooks/useUser';
import {
  GiftCard,
  GiftCardCreateResponse,
  GiftCardCreateSuccess,
  GiftCardDiscountResponse,
  GiftCardProductResponse,
  GiftCardThemeType,
  GiftCardUploadCsvPayload,
  SuccessResponse
} from 'services/api';
import CookieService from 'services/cookie/cookie';
import { FTUE_FLAG_TYPES } from 'services/cookie/types';
import { dispatchGaEvent, PageType } from 'services/google-analytics';
import { AppRoute } from 'share/types/routes';
import { useFeatureFlags } from 'src/contexts/feature-flags';
import useDebounce from 'src/hooks/useDebounce';
import { GiftCardQueryKeys } from 'src/query-keys/giftCard';
import { calculateGiftCardCart } from 'src/utils/calculate-giftcard-cart/calculate-giftcard-cart';
import { FeatureFlag } from 'src/utils/constants';
import generateQuery from 'utils/generate-query';

import { AddGiftCard } from './AddGiftCard';
import { LabelText } from './constants';
import { GiftCardListingActionMenu } from './GiftCardListingActionMenu';
import { GiftCardListingColumns } from './GiftCardListingColumns';
import { GiftCardOrderConfirmation } from './GiftCardOrderConfirmation';
import { GiftCardPaymentFlow } from './GiftCardPaymentFlow';
import { GiftCardSummaryPage } from './GiftCardSummaryPage';
import { OrderSummaryType } from './OrderSummary/types';
import { GiftCardAction } from './types';

import './styles.scss';

import { ActionProps } from '../Page/types';

export function GiftCardContainer() {
  const INIT_ORDER_SUMMARY_VALUES = {
    quantity: 1,
    giftCardValue: undefined,
    giftCardMessage: FormLabelText.MESSAGE_DEFAULT,
    total: 0,
    subtotal: 0
  };

  const api = useApi();
  const config = useConfiguration();
  const queryClient = useQueryClient();
  const featureFlags = useFeatureFlags();
  const navigate = useNavigate();
  const user = useUser();
  const { pushNotification, dismissNotification } = useNotification();
  const [, setAction, { id: giftCardId }] = useAction<GiftCardAction>();
  const [enableSubmit, setEnableSubmit] = useState<boolean>(false);
  const [runTour, setRunTour] = useState<boolean>(false);
  const [csvRecipients, setCsvRecipients] = useState<{
    file: File | null;
    length: number;
  }>();
  const [selectedCard, setSelectedCard] = useState<GiftCardThemeType | null>(
    null
  );
  const [orderSummary, setOrderSummary] = useState<OrderSummaryType>(
    INIT_ORDER_SUMMARY_VALUES
  );
  const [giftCardCreateResponse, setGiftCardCreateResponse] =
    useState<GiftCardCreateSuccess>();
  const [formPayload, setFormPayload] = useState<{
    formValues: GiftCardPurchaseFormValues;
    actions: FormikHelpers<GiftCardPurchaseFormValues>;
  }>();

  const csvColumns: CsvColumn[] = [
    {
      key: 'name',
      label: 'Name',
      required: true
    },
    {
      key: 'email',
      label: 'Email address',
      required: true
    }
  ];

  useEffect(() => {
    dispatchGaEvent({ pageType: PageType.GIFT_CARD });
  }, []);

  const steps = [
    {
      target: '.gift-card-spotlight-products',
      content: LabelText.GIFT_CARD_FTUE.FIRST_STEP_CONTENT,
      placement: 'top',
      disableBeacon: true
    },
    {
      target: '.gift-card-spotlight-form-value',
      content: LabelText.GIFT_CARD_FTUE.SECOND_STEP_CONTENT
    },
    {
      target: '.gift-card-spotlight-form-recipients',
      content: LabelText.GIFT_CARD_FTUE.THIRD_STEP_CONTENT
    },
    {
      target: '.gift-card-spotlight-form-message',
      content: LabelText.GIFT_CARD_FTUE.FOURTH_STEP_CONTENT
    }
  ];

  const columns: Column<GiftCard>[] = useMemo(
    () => GiftCardListingColumns(config.currencyCode),
    [config.currencyCode]
  );

  // params
  const queryParams = useQueryParam();

  const page = queryParams.offset ? parseInt(queryParams.offset) : undefined;
  const rowsPerPage = queryParams.count
    ? parseInt(queryParams.count)
    : undefined;
  const tableListProps = {
    ...(page && {
      page: Math.floor(page / (rowsPerPage || RowsPerPage.TWENTYFIVE))
    }),
    ...(rowsPerPage && { rowsPerPage })
  };

  const filters = ['search', 'count', 'offset'];

  const filterParams = Object.keys(queryParams)
    .filter((key) => filters.includes(key))
    .reduce((params: Record<string, string>, key: string) => {
      params[key] = queryParams[key];
      return params;
    }, {});

  const handlePageChange = (
    changes: Record<string, string | number | undefined>
  ) => {
    let params = {
      ...queryParams,
      ...changes
    };

    params = Object.keys(params).reduce(
      (acc, key) => {
        params[key] !== undefined && (acc[key] = params[key]);
        return acc;
      },
      {} as Record<string, any>
    );

    navigate(
      {
        pathname: AppRoute.GiftCard,
        search: generateQuery(params)
      },
      { replace: true }
    );
  };

  const handlePaginationChange = (page: number, rowsPerPage: number) => {
    handlePageChange({
      ...{
        offset: page ? page * rowsPerPage : undefined,
        count: rowsPerPage
      }
    });
  };

  const isPendingVerification =
    user.company.is_company_pending_gift_card_verification;

  const handleFormReset = () => {
    setSelectedCard(null);
    setEnableSubmit(false);
    setCsvRecipients(undefined);
    setOrderSummary(INIT_ORDER_SUMMARY_VALUES);
  };

  // queries
  const getGiftCardProducts = useQueryWrapperP1<GiftCardProductResponse, Error>(
    GiftCardQueryKeys.detail(),
    () => api.company.giftCard.get_products(),
    {
      enabled: !isPendingVerification, // enable gc products fetching only for verified companies
      onError: () => {
        pushNotification('Failed to get gift card products', {
          variant: 'error',
          autoHideDuration: 5000
        });
      }
    }
  );
  const giftCardSummaryQuery = useQueryWrapperP1(
    GiftCardQueryKeys.detail(parseInt(giftCardId)),
    () => api.company.giftCard.get(parseInt(giftCardId)),
    { enabled: Boolean(giftCardId) }
  );

  const getGiftCardList = useQueryWrapperP0(
    GiftCardQueryKeys.list(filterParams),
    () => api.company.giftCard.list(filterParams),
    {
      enabled: !isPendingVerification, // enable gc list fetching only for verified companies
      onError: () => {
        pushNotification('Failed to get gift cards', {
          variant: 'error',
          autoHideDuration: 5000
        });
      }
    }
  );

  const giftCardProduct =
    getGiftCardProducts.data?.data && getGiftCardProducts.data?.data.length
      ? getGiftCardProducts.data?.data[0]
      : { sku: '' };

  const sku = giftCardProduct.sku;
  const quantity = orderSummary.quantity ?? 0;
  const value = orderSummary.giftCardValue ?? 0;

  const fetchGiftCardDiscount = useQueryWrapperP1<
    GiftCardDiscountResponse,
    Error
  >(
    GiftCardQueryKeys.discount(sku, quantity, value),
    () => api.company.giftCard.fetchDiscount({ sku, quantity, value }),
    {
      enabled: Boolean(sku && quantity && value)
    }
  );

  const discountAmount = fetchGiftCardDiscount.data?.data.discount_amount ?? 0;
  const discountPercentage =
    fetchGiftCardDiscount.data?.data.discount_percentage ?? 0;

  useEffect(() => {
    const { total, subtotal } = calculateGiftCardCart({
      discountAmount,
      giftCardAmount: orderSummary.giftCardValue ?? 0,
      quantity: orderSummary.quantity ?? 0
    });

    setOrderSummary((state) => {
      return {
        ...state,
        total,
        subtotal,
        discountAmount,
        discountPercentage
      };
    });
  }, [
    discountAmount,
    discountPercentage,
    orderSummary.giftCardValue,
    orderSummary.quantity
  ]);

  const createGiftCard = useMutation<GiftCardCreateResponse, Error, FormData>(
    async (payload) => {
      return api.company.giftCard.create(payload);
    }
  );

  const uploadReceipt = useMutation<
    SuccessResponse,
    Error,
    GiftCardUploadCsvPayload
  >(async (payload) => {
    return api.company.giftCard.uploadReceipt(payload);
  });

  const uploadUpdatedEmailCsv = useMutation<
    SuccessResponse,
    Error,
    GiftCardUploadCsvPayload
  >(async (payload) => {
    return api.company.giftCard.uploadUpdatedEmailCsv(payload);
  });

  const validateForm = (
    formValues: GiftCardPurchaseFormValues,
    actions: FormikHelpers<GiftCardPurchaseFormValues>
  ) => {
    setAction(GiftCardAction.AddConfirm);
    setFormPayload({ formValues: formValues, actions: actions });
  };

  const detailFormRef = useRef<FormikProps<GiftCardPurchaseFormValues>>(null);

  const handleAddGiftCard = () => {
    if (!formPayload) return;
    const PAYLOAD_KEY = 'payload';
    const FILE_KEY = 'csv_recipients';
    const formData = new FormData();

    const { value, quantity } = formPayload.formValues;
    const { subtotal, total } = calculateGiftCardCart({
      discountAmount: orderSummary.discountAmount as number,
      giftCardAmount: value as number,
      quantity: quantity as number
    });

    const orderPayload: GiftCardPurchaseFormValues = {
      ...formPayload.formValues,
      subtotal,
      total,
      discount_amount: orderSummary.discountAmount,
      discount_percentage: orderSummary.discountPercentage
    };

    formData.append(PAYLOAD_KEY, JSON.stringify(orderPayload));
    csvRecipients && formData.append(FILE_KEY, csvRecipients.file as File);

    const createInProgress = pushNotification(`Adding Gift Card...`);

    createGiftCard.mutate(formData, {
      onSuccess: (data) => {
        dismissNotification(createInProgress);
        pushNotification(`Gift Card added!`, {
          variant: 'success'
        });
        setGiftCardCreateResponse(data.data);
        setSelectedCard(null);
        formPayload.actions.resetForm();
        setAction(GiftCardAction.Add, false);
        setAction(GiftCardAction.AddConfirm, false);
        setAction(GiftCardAction.OrderConfirmation);
        queryClient.invalidateQueries(GiftCardQueryKeys.list());
        uploadReceipt.reset();
      },
      onError: (e) => {
        dismissNotification(createInProgress);
        pushNotification(e.message, {
          variant: 'error',
          onClick: () => detailFormRef.current?.submitForm()
        });
      },
      onSettled: () => {
        dismissNotification(createInProgress);
        formPayload.actions.setSubmitting(false);
      }
    });
  };

  const handleUploadReceipt = (id: number, receipt: File | null) => {
    if (!receipt) return;

    const PAYLOAD_KEY = 'payment_receipt';
    const formData = new FormData();

    formData.append(PAYLOAD_KEY, receipt as File);

    const payload = { id: id, formData: formData };

    uploadReceipt.mutate(payload);
  };

  const handleUploadUpdatedEmailCsv = (id: number) => {
    const PAYLOAD_KEY = 'failed_recipients_reupload';
    const formData = new FormData();

    csvRecipients && formData.append(PAYLOAD_KEY, csvRecipients.file as File);

    const payload = { id: id, formData: formData };

    uploadUpdatedEmailCsv.mutate(payload, {
      onSuccess: () => {
        setAction(GiftCardAction.CsvUploadFailed, false);
      },
      onError: (e) => {
        setAction(GiftCardAction.CsvUploadFailed, false);
        pushNotification(e.message, {
          variant: 'error'
        });
      }
    });
  };

  const handleDownloadRecipientCsv = () => {
    api.company.giftCard
      .downloadCsvRecipients(parseInt(giftCardId))
      .then((data) => {
        const url = window.URL.createObjectURL(
          new Blob([data as unknown as BlobPart])
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `recipients.csv`);
        document.body.appendChild(link);
        link.click();
        pushNotification('Recipient list downloaded', {
          variant: 'success'
        });
      })
      .catch((e) => {
        const error = e as Error;
        pushNotification(error.message, {
          variant: 'error'
        });
      });
  };

  const handleSendGiftCardDetails = (id = giftCardId) => {
    api.company.giftCard
      .sendGiftCardDetailEmail(parseInt(id))
      .then(() =>
        pushNotification('Email sent!', {
          variant: 'success'
        })
      )
      .catch((e) => {
        const error = e as Error;
        pushNotification(error.message, {
          variant: 'error'
        });
      });
  };

  const handleDownloadClick = () => {
    api.company.giftCard
      .downloadReport()
      .then((data: unknown) => {
        const url = window.URL.createObjectURL(new Blob([data as Blob]));
        const link = document.createElement('a');
        link.href = url;
        const datetime = new Date();
        const _date = datetime.toLocaleDateString('en-US', {
          timeZone: config.timezone,
          month: 'long',
          day: '2-digit',
          year: 'numeric'
        });
        const _time = datetime.toLocaleTimeString('en-US', {
          timeZone: config.timezone,
          hour: '2-digit',
          minute: '2-digit'
        });
        link.setAttribute(
          'download',
          `${_date + '_' + _time.replace(/\:/g, '')}.csv`
        );
        document.body.appendChild(link);
        link.click();
        pushNotification('Gift Card Report Downloaded', {
          variant: 'success'
        });
      })
      .catch((e) => {
        const error = e as Error;
        const errorSnackbarId = pushNotification(error.message, {
          variant: 'error',
          onClick: () => dismissNotification(errorSnackbarId)
        });
      });
  };

  const handleDownloadErroneousCsv = () => {
    api.company.giftCard
      .downloadErroneousCsv(parseInt(giftCardId))
      .then((data) => {
        const url = window.URL.createObjectURL(
          new Blob([data as unknown as BlobPart])
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `recipients.csv`);
        document.body.appendChild(link);
        link.click();
        pushNotification('Recipient list downloaded', {
          variant: 'success'
        });
      })
      .catch((e) => {
        const error = e as Error;
        pushNotification(error.message, {
          variant: 'error'
        });
      });
  };

  const renderPaymentFlowModalHeader = () => {
    return (
      <div className="dialog">
        <div className="dialog__content">
          <div className="dialog__content__title">
            <IcWallet fill="interaction-primary" />
            <Typography type="title-md" color="neutral-primary" marginLeft="xs">
              {LabelText.GIFT_CARD_PAYMENT_FLOW.TITLE}
            </Typography>
          </div>
          <Typography type="paragraph-md" color="neutral-primary">
            {LabelText.GIFT_CARD_PAYMENT_FLOW.DESCRIPTION}
          </Typography>
        </div>
        <ButtonCircular
          ariaLabel="close-dialog-button"
          onClick={() => setAction(GiftCardAction.PaymentFlow, false)}
        >
          <IcClose fill="#E21B70" />
        </ButtonCircular>
      </div>
    );
  };

  return (
    <Page
      actions={{
        [GiftCardAction.Add]: {
          component: (
            <AddGiftCard
              tourSteps={steps}
              runTour={runTour}
              resetTour={() => setRunTour(false)}
              config={config}
              isLoading={getGiftCardProducts.isLoading}
              data={getGiftCardProducts.data?.data ?? []}
              csvRecipients={csvRecipients}
              handleCsvUploadCancel={() => setCsvRecipients(undefined)}
              detailFormRef={detailFormRef}
              enableSubmit={enableSubmit}
              handleFormReset={handleFormReset}
              onActionClick={setAction}
              onSubmit={validateForm}
              orderSummary={orderSummary}
              selectedCard={selectedCard}
              toggleSubmit={(value: boolean) => setEnableSubmit(value)}
              handleOrderSummaryChange={(
                changes: Partial<OrderSummaryType>
              ) => {
                const giftCardAmount =
                  (changes.giftCardValue || orderSummary.giftCardValue) ?? 0;
                const quantity =
                  (changes.quantity || orderSummary.quantity) ?? 0;

                const { total, subtotal } = calculateGiftCardCart({
                  discountAmount: 0,
                  giftCardAmount,
                  quantity
                });

                setOrderSummary({
                  ...orderSummary,
                  ...changes,
                  total,
                  subtotal
                });
              }}
              handleSelectCard={(theme: GiftCardThemeType) =>
                setSelectedCard(theme)
              }
              placeOrderLoading={createGiftCard.isLoading}
              discount={{
                isLoading: fetchGiftCardDiscount.isLoading,
                data: fetchGiftCardDiscount.data?.data ?? {
                  discount_amount: 0,
                  discount_percentage: 0
                }
              }}
            />
          ),
          type: ActionType.FullDialog,
          options: {
            title: LabelText.ADD_GIFT_CARD.TITLE,
            action: {
              label: LabelText.ADD_GIFT_CARD.ADD_BUTTON,
              callback: () => detailFormRef.current?.handleSubmit(),
              type: 'submit',
              disabled: !enableSubmit,
              loading:
                createGiftCard.isLoading || fetchGiftCardDiscount.isLoading
            }
          }
        },
        [GiftCardAction.AddConfirm]: {
          component: (
            <>
              <Button kind="secondary">
                {LabelText.ADD_CONFIRM_MODAL.CANCEL_BUTTON}
              </Button>
              <Button kind="primary" onClick={() => handleAddGiftCard()}>
                {LabelText.ADD_CONFIRM_MODAL.CONFIRM_BUTTON}
              </Button>
            </>
          ),
          type: ActionType.AppDialog,
          options: {
            title: LabelText.ADD_CONFIRM_MODAL.TITLE,
            content: (
              <>
                You are placing an order for{' '}
                <b>
                  {orderSummary.quantity} x {config.currencyCode}
                  {orderSummary.giftCardValue}
                </b>{' '}
                gift card worth{' '}
                <b>
                  {config.currencyCode}
                  {orderSummary.total}
                </b>
                .
                {Boolean(orderSummary.discountAmount) && (
                  <div>
                    You received a discount of{' '}
                    <b>
                      {config.currencyCode} {orderSummary.discountAmount}
                    </b>{' '}
                    for this transaction.
                  </div>
                )}
                <br />
                Once your order is placed, it cannot be changed.
              </>
            )
          }
        },
        [GiftCardAction.View]: {
          component: (
            <div style={{ width: '850px' }}>
              <GiftCardSummaryPage
                summaryQuery={giftCardSummaryQuery}
                handleDownloadRecipientCsv={handleDownloadRecipientCsv}
                handleSendGiftCardDetails={handleSendGiftCardDetails}
                onActionClick={setAction}
                uploadSuccessStatus={{
                  error: uploadUpdatedEmailCsv.isError,
                  success: uploadUpdatedEmailCsv.isSuccess
                }}
              />
            </div>
          ),
          type: ActionType.FullDialog,
          options: {
            title: LabelText.GIFT_CARD_SUMMARY_PAGE.TITLE
          }
        },
        [GiftCardAction.CsvUpload]: {
          component: (
            <CsvUpload
              config={config as Configuration}
              columns={csvColumns}
              fileHandlerCallback={(file, validRows) =>
                setCsvRecipients({ file: file, length: validRows })
              }
              templateEndpoint="/company-portal/giftcard/recipients-template"
              handleActionClick={() =>
                setAction(GiftCardAction.CsvUpload, false)
              }
              successMessageMultiple={
                LabelText.GIFT_CARD_RECIPIENTS.SUCCESS_MESSAGE_MULTIPLE
              }
              successMessageSingle={
                LabelText.GIFT_CARD_RECIPIENTS.SUCCESS_MESSAGE_SINGLE
              }
              errorMessage={LabelText.GIFT_CARD_RECIPIENTS.ERROR_MESSAGE}
            />
          ),
          type: ActionType.RightDrawer,
          options: {
            title: LabelText.GIFT_CARD_RECIPIENTS.TITLE
          }
        },
        [GiftCardAction.OrderConfirmation]: {
          component: (
            <div style={{ width: '850px' }}>
              <GiftCardOrderConfirmation
                config={config}
                data={giftCardCreateResponse}
                onActionClick={setAction}
                orderSummary={orderSummary}
                uploadState={uploadReceipt.status}
                handleUpload={handleUploadReceipt}
              />
            </div>
          ),
          type: ActionType.FullDialog,
          options: {
            title: LabelText.GIFT_CARD_ORDER_CONFIRMATION.TITLE
          }
        },
        [GiftCardAction.DiscountFetchError]: {
          component: (
            <Button
              kind="primary"
              onClick={() =>
                setAction(GiftCardAction.DiscountFetchError, false)
              }
            >
              {LabelText.DISCOUNT_FETCH_ERROR_MODAL.CONFIRM_BUTTON}
            </Button>
          ),
          type: ActionType.AppDialog,
          options: {
            title: LabelText.DISCOUNT_FETCH_ERROR_MODAL.TITLE,
            content: <>{LabelText.DISCOUNT_FETCH_ERROR_MODAL.DESCRIPTION}</>
          }
        },
        ...(featureFlags(FeatureFlag.PaymentFlow) && {
          [GiftCardAction.PaymentFlow]: {
            component: (
              <Button
                kind="primary"
                onClick={() => setAction(GiftCardAction.PaymentFlow, false)}
              >
                {LabelText.GIFT_CARD_PAYMENT_FLOW.DONE_BUTTON}
              </Button>
            ),
            type: ActionType.AppDialog,
            options: {
              title: renderPaymentFlowModalHeader(),
              content: giftCardSummaryQuery.data ? (
                <GiftCardPaymentFlow
                  details={config.qwikcilver_bank_details}
                  id={giftCardSummaryQuery.data.data.id}
                  external_order_number={
                    giftCardSummaryQuery.data.data.external_order_number
                  }
                  uploadState={uploadReceipt.status}
                  handleUpload={handleUploadReceipt}
                  isModal
                />
              ) : (
                ''
              )
            }
          }
        }),
        ...(featureFlags(FeatureFlag.ResendFailedEmail) && {
          [GiftCardAction.CsvUploadFailed]: {
            component: giftCardSummaryQuery.data && (
              <CsvUpload
                config={config as Configuration}
                columns={csvColumns}
                failedUpload={{
                  recipients:
                    giftCardSummaryQuery.data.data.number_of_failed_recipients,
                  handleDownloadErroneousCsv
                }}
                fileHandlerCallback={(file, validRows) =>
                  setCsvRecipients({ file: file, length: validRows })
                }
                handleActionClick={() =>
                  handleUploadUpdatedEmailCsv(giftCardSummaryQuery.data.data.id)
                }
                successMessageMultiple={
                  LabelText.UPLOAD_CSV_FAILED.SUCCESS_MESSAGE_MULTIPLE
                }
                successMessageSingle={
                  LabelText.UPLOAD_CSV_FAILED.SUCCESS_MESSAGE_SINGLE
                }
                errorMessage={LabelText.GIFT_CARD_RECIPIENTS.ERROR_MESSAGE}
              />
            ),
            type: ActionType.RightDrawer,
            options: {
              title: LabelText.UPLOAD_CSV_FAILED.TITLE
            }
          }
        })
      }}
    >
      <PageHeader
        title={LabelText.TITLE}
        buttons={
          isPendingVerification
            ? []
            : [
                <Button
                  key="2"
                  kind="secondary"
                  onClick={() => {
                    handleDownloadClick();
                  }}
                >
                  {LabelText.DOWNLOAD_BUTTON}
                </Button>,
                <Button
                  key="1"
                  kind="primary"
                  type="button"
                  onClick={() => {
                    CookieService.isFirstTimeUser(
                      FTUE_FLAG_TYPES.GIFT_CARD_PURCHASE_FORM
                    ) && setRunTour(true);
                    setAction(GiftCardAction.Add);
                  }}
                >
                  {LabelText.ADD_BUTTON}
                </Button>
              ]
        }
      />
      {isPendingVerification ? (
        <CallToActions
          illustration={(props: SVGProps<SVGSVGElement>) => (
            <PendingVerification {...props} />
          )}
          title={LabelText.GIFT_CARD_LISTING.VERIFICATION_CTA_TITLE}
          subtitle={LabelText.GIFT_CARD_LISTING.VERIFICATION_CTA_SUBTITLE}
        ></CallToActions>
      ) : (
        <TableList<GiftCard>
          total={getGiftCardList.error ? 0 : getGiftCardList.data?.total ?? -1}
          loading={getGiftCardList.isLoading}
          empty={
            <CallToActions
              illustration={(props: SVGProps<SVGSVGElement>) => (
                <ReferAFriend {...props} />
              )}
              title={LabelText.GIFT_CARD_LISTING.CTA_TITLE}
              subtitle={LabelText.GIFT_CARD_LISTING.CTA_SUBTITLE}
            >
              <Button
                kind="primary"
                type="button"
                onClick={() => {
                  CookieService.isFirstTimeUser(
                    FTUE_FLAG_TYPES.GIFT_CARD_PURCHASE_FORM
                  ) && setRunTour(true);
                  setAction(GiftCardAction.Add);
                }}
              >
                {LabelText.GIFT_CARD_LISTING.CTA_BUTTON}
              </Button>
            </CallToActions>
          }
          columns={columns}
          data={getGiftCardList.data?.data || []}
          onPaginationChange={handlePaginationChange}
          action={(giftCard) => (
            <ActionMenu>
              <GiftCardListingActionMenu
                giftCard={giftCard}
                handleView={() =>
                  setAction(GiftCardAction.View, true, {
                    id: giftCard.id.toString()
                  })
                }
                handleSendEmail={() =>
                  handleSendGiftCardDetails(giftCard.id.toString())
                }
              />
            </ActionMenu>
          )}
          {...tableListProps}
        >
          <TableFilterRow>
            <SearchInput
              value={(queryParams.search as string) || ''}
              onSearch={(value: string) => {
                handlePageChange({
                  search: value || undefined,
                  offset: undefined
                });
              }}
              placeholder={LabelText.GIFT_CARD_LISTING.SEARCH_PLACEHOLDER}
            />
          </TableFilterRow>
        </TableList>
      )}
    </Page>
  );
}
